import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiPlus, FiRefreshCw, FiX } from 'react-icons/fi';
import { tasksAPI, filesAPI } from '../api';
import TaskList from './TaskList';

function ScrapeDetail() {
  const { taskId } = useParams();
  const navigate = useNavigate();
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showNewTranslation, setShowNewTranslation] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [modalImage, setModalImage] = useState(null);

  const loadTask = async () => {
    try {
      const data = await tasksAPI.getTaskDetails(taskId);
      setTask(data);
    } catch (error) {
      console.error('Failed to load task:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTask();
    // Auto-refresh every 3 seconds
    const interval = setInterval(loadTask, 3000);
    return () => clearInterval(interval);
  }, [taskId]);

  const handleNewTranslation = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await tasksAPI.startTranslation(taskId, selectedLanguage);
      setSelectedLanguage('');
      setShowNewTranslation(false);
      loadTask();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to start translation');
    } finally {
      setSubmitting(false);
    }
  };

  const availableLanguages = [
    { code: 'en-US', name: 'English' },
    { code: 'es-ES', name: 'Spanish' },
    { code: 'it-IT', name: 'Italian' },
    { code: 'nl-NL', name: 'Dutch' },
    { code: 'de-DE', name: 'German' },
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem' }}>
        <span className="spinner" style={{ width: '2rem', height: '2rem' }}></span>
      </div>
    );
  }

  if (!task || task.task_type !== 'scrape') {
    return (
      <div className="alert alert-error">
        Scrape task not found
      </div>
    );
  }

  const metadata = task.metadata || {};
  const sections = metadata.sections || [];

  return (
    <div>
      <div style={{ marginBottom: '2rem' }}>
        <button className="btn btn-secondary" onClick={() => navigate('/')}>
          <FiArrowLeft />
          Back to Dashboard
        </button>
      </div>

      {/* Task Header */}
      <div className="detail-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
          <div style={{ flex: 1, marginRight: '1rem' }}>
            <h2>Scraping Task #{task.id}</h2>
            <p style={{ 
              color: '#6b7280', 
              marginTop: '0.5rem',
              wordBreak: 'break-all',
              overflowWrap: 'anywhere'
            }}>
              {task.url}
            </p>
          </div>
          <button className="btn btn-secondary" onClick={loadTask}>
            <FiRefreshCw />
            Refresh
          </button>
        </div>
      </div>

      {/* Scraped Content */}
      <div className="detail-grid">
        <div className="detail-section">
          <h3>Product Information</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div>
              <strong>Title:</strong>
              <p>{metadata.title || 'N/A'}</p>
            </div>
            <div>
              <strong>Product Description:</strong>
              <p>{metadata.product_description || 'N/A'}</p>
            </div>
          </div>
        </div>

        <div className="detail-section">
          <h3>About This Item</h3>
          {metadata.about_this_item && metadata.about_this_item.length > 0 ? (
            <ul style={{ marginTop: '0.5rem', paddingLeft: '1.5rem' }}>
              {metadata.about_this_item.map((item, index) => (
                <li key={index} style={{ marginBottom: '0.5rem' }}>{item}</li>
              ))}
            </ul>
          ) : (
            <p style={{ color: '#6b7280' }}>No information available</p>
          )}
        </div>
      </div>

      {/* Product Images */}
      {metadata.product_images && metadata.product_images.length > 0 && (
        <div className="card">
          <h3 className="card-title">Product Images ({metadata.product_images.length})</h3>
          <div className="image-gallery">
            {metadata.product_images.map((image, index) => (
              <div 
                key={index} 
                className="image-item clickable"
                onClick={() => setModalImage({
                  src: filesAPI.getFileUrl(`${task.directory}/${image.filename}`),
                  alt: `Product ${index + 1}`
                })}
              >
                <img 
                  src={filesAPI.getFileUrl(`${task.directory}/${image.filename}`)} 
                  alt={`Product ${index + 1}`}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* A+ Content Images */}
      {metadata.aplus_images && metadata.aplus_images.length > 0 && (
        <div className="card">
          <h3 className="card-title">A+ Content Images ({metadata.aplus_images.length})</h3>
          <div className="image-gallery">
            {metadata.aplus_images.map((image, index) => (
              <div 
                key={index} 
                className="image-item clickable"
                onClick={() => setModalImage({
                  src: filesAPI.getFileUrl(`${task.directory}/${image.filename}`),
                  alt: `A+ Content ${index + 1}`
                })}
              >
                <img 
                  src={filesAPI.getFileUrl(`${task.directory}/${image.filename}`)} 
                  alt={`A+ Content ${index + 1}`}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Translations */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Translations</h3>
          <button 
            className="btn btn-primary"
            onClick={() => setShowNewTranslation(!showNewTranslation)}
          >
            <FiPlus />
            New Translation
          </button>
        </div>

        {showNewTranslation && (
          <form onSubmit={handleNewTranslation} style={{ marginBottom: '1rem' }}>
            <div className="form-group">
              <label className="form-label" htmlFor="language">
                Target Language
              </label>
              <select
                id="language"
                className="form-select"
                value={selectedLanguage}
                onChange={(e) => setSelectedLanguage(e.target.value)}
                required
              >
                <option value="">Select a language</option>
                {availableLanguages.map(lang => (
                  <option key={lang.code} value={lang.code}>
                    {lang.name}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <span className="spinner"></span>
                    Starting...
                  </>
                ) : (
                  'Start Translation'
                )}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setShowNewTranslation(false);
                  setSelectedLanguage('');
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        {task.translations && (
          <TaskList 
            tasks={task.translations.map(t => ({ ...t, task_type: 'translation' }))} 
            onUpdate={loadTask} 
          />
        )}
      </div>

      {/* Image Modal */}
      {modalImage && (
        <div 
          className="image-modal-overlay" 
          onClick={() => setModalImage(null)}
        >
          <div className="image-modal-content">
            <button 
              className="image-modal-close"
              onClick={(e) => {
                e.stopPropagation();
                setModalImage(null);
              }}
            >
              <FiX size={24} />
            </button>
            <img 
              src={modalImage.src} 
              alt={modalImage.alt}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default ScrapeDetail; 